import { Dispatch, ReactElement, SetStateAction } from "react"
import { ColumnShape } from "react-base-table"

export interface IStepData {
  filters: IDataUpload
  showNewMapping: boolean
}

export interface ISubStep {
  id: number
  label: string
  content: ReactElement
  description?: string
  isVisible?: (data: IStepData) => boolean
  showMappedField?: boolean
}

export interface IStep {
  id: number
  label: string
  content: ReactElement
  substeps?: ISubStep[]
  description?: string
  wrapper?: (props: { mappingSchemaId: number; children: ReactElement }) => ReactElement
  nextAction?: string
  showMappedField?: boolean
}

export interface IDataUpload {
  adm0Code: number
  file: File | null
  note?: string
  mappingSchemaId?: number
  mappingSchemaName?: string
  reportId?: number
}

export interface IReportObs {
  additionalMetadata: string
  commodityId: number | null
  commodityName: string
  commodityPriceDate: string
  commodityPriceObservationId: number
  commodityPriceSourceName: string
  commodityPriceValue: number
  commodityUnitId: number | null
  commodityUnitName: string
  currencyId: number | null
  currencyName: string
  errorDescription: string | null
  fetchingOperationId: number | null
  marketId: number | null
  marketName: string
  observationDate: string | null
  observationStatusId: number
  priceTypeId: number | null
  priceTypeName: string
  reportId: number | null
  uuid: string | null
  isOutlier: boolean
  seriesCombinationCode?: string
  lowerBound?: number
  upperBound?: number
  q1?: number
  q3?: number
  iqr?: number
  MoM?: number
  YoY?: number
}

export enum DataUploadSteps {
  DATA = 1,
  MAPPING = 2,
  VALIDATION = 3,
  FINALIZE = 4,
}

export enum MappingSteps {
  HEADER = 1,
  FIELDS = 2,
  GLOBAL = 3,
}

export enum ExcelMappingSteps {
  DATA = 1,
  HEADER = 2,
  FIELDS = 3,
  GLOBAL = 4,
}

export enum ObservationStatus {
  VALID = 1,
  INVALID_KEYS = 2,
  INVALID_SERIES = 3,
  INVALID_DATE = 4,
  DUPLICATED = 5,
  DISCARDED = 7,
  APPROVED = 8,
}

export type ValidationTableProps = {
  data: IReportObs[]
  onEdit?: (item: IReportObs) => void
  onDetails?: (item: IReportObs) => void
  onDelete?: (item: IReportObs) => void
  selectable?: boolean
  selectedItems?: number[]
  onSelectionChange?: Dispatch<SetStateAction<number[]>>
  updatedItems?: IReportObs[]
  extraColumns?: ColumnShape<IReportObs>[]
  fullscreen?: boolean
  columnsKey?: string
}

export interface DetailsModalProps {
  open: boolean
  item: IReportObs | null
  onClose: () => void
  onSubmit?: (items: IReportObs[]) => void
}

export interface EditModalProps {
  open: boolean
  items: IReportObs[]
  originalItems: IReportObs[]
  onClose: () => void
  onSubmit: (items: IReportObs[]) => void
}

export type SectionColor = "red" | "teal" | "yellow" | "violet" | "gray"

export interface ValidationSection {
  label: string
  color: SectionColor
  statusId: number
  details?: boolean
  edit?: boolean
  delete?: boolean
  selectable?: boolean
  description?: string
}

export interface SectionStyles {
  bg: Record<SectionColor, string>
  text: Record<SectionColor, string>
}

export interface ReportData {
  validCount: number
  invalidKeysCount: number
  invalidSeriesCount: number
  invalidDateCount: number
  duplicatedCount: number
  discardedCount: number
  commodities: Record<number, boolean>
  markets: Record<number, boolean>
  approvedGroups: Record<string, IReportObs[]>
}

export interface IOutlierDetection {
  previousMonthPrice?: number
  previousYearPrice?: number
  seriesCombination: string
  month: number
  year: number
}

export interface IPriceDataSummary {
  countries: {
    name: string
    adm0Code: number
    priceData: { month: number; approved: number; pending: number; rejected: number }[]
  }[]
}

export interface ILastFetchData {
  country: string
  mappingSchema: string
  mappingSchemaId: string
  lastSubmissionTime: string
  status: string
  fetchingResponseCodeId: number
  totalReports: number
  totalRecords: string
}

export interface IFetchData {
  country: string
  mappingSchema: string
  mappingSchemaId: string
  runAt: string
  fetchingResponseCodeId: number
  message: string
}

export enum ModaUploadSteps {
  CONFIG = 0,
  CONNECT = 1,
  VALIDATION = 2,
  FINALIZE = 3,
}

export interface IModaFetchData {
  apiKey: number
  projectId: number
  formId: number
  frequencyId: number
  launchAndForget: false
  mappingSchemaId: number
  dataCollectionFrequencyId: number
  endDate: string
  dataCollectionStartDate: string
  dataCollectionEndDate: string
}

export type OutliersFilters = {
  adm0Code: number
  marketId?: number
  commodityId?: number
  priceTypeId?: number
  startDate?: string | null
  endDate?: string | null
  monthlyThreshold?: number
  yearlyThreshold?: number
  skip: number
  take: number
}

export interface IOutliersData {
  commodityPriceId: number
  commodityId: number
  commodityName: string
  marketId: number
  marketName: string
  priceTypeId: number
  priceTypeName: string
  commodityUnitId: number
  commodityUnitName: string
  currencyId: number
  currencyName: string
  reportId: number
  commodityPriceStatusId: number
  dataCollectionFrequencyId: number
  adm0Code: number
  commodityPriceValue: number
  commodityPriceSourceName: string
  commodityPriceMetadata: string
  commodityPriceDate: string
  seriesCombinationCode: string
  commodityPriceObservations: number
  commodityPriceDateWeek: number
  commodityPriceDateMonth: number
  commodityPriceCreateDate: string
  outlierMinValue: number
  outlierMaxValue: number
  outlierAvgValue: number
  lastUpdateDate: string
  yoYpercentage: number
  moMpercentage: number
  isQuartileOutlier: boolean
}
